/**
 * engraving-personalize.js
 *
 * Client behaviour for the in-page PDP engraving section
 * (sections/engraving-personalize.liquid).
 *
 * Responsibilities:
 *   1. Mirror the currently selected variant from the main product form into
 *      our hidden `name="id"` input, so the engraving flow always adds the
 *      actively-selected variant to the cart.
 *   2. Validate the uploaded handwriting scan (type + size) before submit.
 *   3. Submit the form asynchronously to /cart/add.js using a FormData body
 *      so multipart file upload works against Shopify's native line-item
 *      property file handling.
 *   4. Update the fee-summary readout when the customer toggles between
 *      One side / Both sides.
 *   5. Format the estimated delivery date in the visitor's locale.
 *   6. On success dispatch the theme's cart:update event so the cart drawer
 *      opens automatically, then reset the form.
 *
 * Written as a plain ES module rather than a Web Component because the
 * section needs no component registry hooks — a single bootstrap per DOM
 * instance is enough.
 */

import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const MAX_FILE_BYTES = 15 * 1024 * 1024; // 15 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const ALLOWED_EXTENSIONS = /\.(jpe?g|png|pdf)$/i;

/**
 * Walk up from the engraving section to find the nearest open product form.
 * Returns the form element whose action matches cart_add_url, or null.
 * @param {HTMLElement} root
 * @returns {HTMLFormElement | null}
 */
function findPdpProductForm(root) {
  // Prefer the product-form-component on the page
  const comp = document.querySelector('product-form-component');
  if (comp) {
    const form = comp.querySelector('form[action*="/cart/add"]');
    if (form instanceof HTMLFormElement) return form;
  }
  // Fallback: first product form in the document outside our own section
  const allForms = document.querySelectorAll('form[action*="/cart/add"]');
  for (const form of allForms) {
    if (form instanceof HTMLFormElement && !root.contains(form)) {
      return form;
    }
  }
  return null;
}

/**
 * Localise a YYYY-MM-DD ISO string into the visitor's locale.
 * Falls back to the ISO string on any error.
 * @param {string} iso
 * @param {string} locale
 */
function formatDate(iso, locale) {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1);
    return dt.toLocaleDateString(locale || 'en', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch (_e) {
    return iso;
  }
}

/**
 * Initialise a single engraving-personalize section.
 * @param {HTMLElement} root
 */
function initEngravingSection(root) {
  const form = /** @type {HTMLFormElement | null} */ (root.querySelector('[data-engraving-form]'));
  if (!form) return;

  const variantInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-engraving-variant-id]'));
  const sides = /** @type {NodeListOf<HTMLInputElement>} */ (root.querySelectorAll('[data-engraving-side]'));
  const feeDisplay = root.querySelector('[data-engraving-fee-display]');
  const fileInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-engraving-file]'));
  const dropzone = root.querySelector('[data-engraving-dropzone]');
  const emptyState = /** @type {HTMLElement | null} */ (root.querySelector('[data-engraving-dropzone-empty]'));
  const filledState = /** @type {HTMLElement | null} */ (root.querySelector('[data-engraving-dropzone-filled]'));
  const filenameEl = root.querySelector('[data-engraving-filename]');
  const removeBtn = root.querySelector('[data-engraving-remove]');
  const uploadError = /** @type {HTMLElement | null} */ (root.querySelector('[data-engraving-upload-error]'));
  const submitError = /** @type {HTMLElement | null} */ (root.querySelector('[data-engraving-submit-error]'));
  const submitBtn = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-engraving-submit]'));
  const submitDefault = root.querySelector('.engraving-personalize__submit-default');
  const submitLoading = root.querySelector('.engraving-personalize__submit-loading');
  const deliveryDisplay = root.querySelector('[data-engraving-delivery-display]');
  const needbyInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-engraving-needby]'));
  const needbyHelp = root.querySelector('[data-engraving-needby-help]');
  const consent = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-engraving-consent]'));
  const consentWrapper = /** @type {HTMLElement | null} */ (root.querySelector('[data-engraving-consent-wrapper]'));
  const consentHint = /** @type {HTMLElement | null} */ (root.querySelector('[data-engraving-consent-hint]'));
  const termsDetails = /** @type {HTMLDetailsElement | null} */ (root.querySelector('[data-engraving-terms]'));
  const termsEnd = root.querySelector('[data-engraving-terms-end]');
  const termsToggleText = root.querySelector('[data-engraving-terms-toggle-text]');
  const sizeScrollBtn = root.querySelector('[data-engraving-scroll-size]');

  const locale = root.dataset.locale || document.documentElement.lang || 'en';
  const minDate = root.dataset.minDate || '';
  const maxDate = root.dataset.maxDate || '';
  const pricePrefixTpl = root.querySelector('[data-engraving-fee-display]')?.textContent?.trim() || '';

  // ---------- Localised delivery date ----------
  if (deliveryDisplay && maxDate) {
    deliveryDisplay.textContent = formatDate(maxDate, locale);
  }

  // Update needby help to use localised dates if they are present as ISO
  if (needbyHelp && minDate && maxDate) {
    const raw = needbyHelp.textContent || '';
    const localizedMin = formatDate(minDate, locale);
    const localizedMax = formatDate(maxDate, locale);
    needbyHelp.textContent = raw
      .replace(minDate, localizedMin)
      .replace(maxDate, localizedMax);
  }

  // ---------- Force-download the order sheet PDF ----------
  // The HTML anchor has a `download` attribute, but browsers silently ignore
  // that for cross-origin resources. Our PDFs live on cdn.shopify.com while
  // the site runs on nouvelheritage.com, so clicking the link just opens the
  // PDF in a new tab instead of downloading it. We intercept the click,
  // fetch the file as a Blob, and trigger a download via an object URL.
  // Falls back to the anchor's default behavior if anything goes wrong so
  // the customer is never left without access to the sheet.
  const downloadLink = /** @type {HTMLAnchorElement | null} */ (
    root.querySelector('.engraving-personalize__download')
  );
  if (downloadLink) {
    downloadLink.addEventListener('click', async (event) => {
      const url = downloadLink.getAttribute('href');
      if (!url) return;
      event.preventDefault();
      const lang = downloadLink.dataset.lang === 'fr' ? 'fr' : 'en';
      const filename =
        lang === 'fr'
          ? 'Nouvel-Heritage_Fiche-Gravure.pdf'
          : 'Nouvel-Heritage_Engraving-Order-Sheet.pdf';
      try {
        const response = await fetch(url, { credentials: 'omit' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Revoke slightly later so Safari has time to start the download.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      } catch (err) {
        // Network error or CORS — fall back to opening in a new tab so the
        // customer can still save manually. Better degraded than blocked.
        console.warn('[engraving] order-sheet download fell back to new tab', err);
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    });
  }

  // ---------- Size reminder → scroll back up ----------
  const prefersReducedMotion =
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  sizeScrollBtn?.addEventListener('click', (event) => {
    event.preventDefault();
    const target =
      document.querySelector('nh-size-guide') ||
      document.querySelector('[data-size-picker]') ||
      document.querySelector('.size-picker-pdp') ||
      document.querySelector('#MainContent');
    if (!target) return;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
    // Try to focus the first focusable control so keyboard users land inside the picker
    const firstFocusable =
      target.querySelector(
        'input:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
    if (firstFocusable instanceof HTMLElement) {
      // Delay focus slightly so the scroll finishes before the browser shifts it back
      window.setTimeout(() => firstFocusable.focus({ preventScroll: true }), 450);
    }
  });

  // ---------- Terms accordion: open/close toggle text + scroll-to-unlock consent ----------
  const setTermsToggleText = (isOpen) => {
    if (!(termsToggleText instanceof HTMLElement)) return;
    const openLabel = termsToggleText.dataset.openLabel || '';
    const closeLabel = termsToggleText.dataset.closeLabel || '';
    termsToggleText.textContent = isOpen ? closeLabel : openLabel;
  };
  setTermsToggleText(termsDetails?.open === true);

  termsDetails?.addEventListener('toggle', () => {
    setTermsToggleText(termsDetails.open);
  });

  /**
   * Unlock the consent checkbox once the customer has seen the end of the
   * important-information block. One-way: once unlocked, stays unlocked.
   */
  let consentUnlocked = false;
  const unlockConsent = () => {
    if (consentUnlocked) return;
    consentUnlocked = true;
    if (consent) {
      consent.disabled = false;
    }
    if (consentWrapper) {
      consentWrapper.classList.add('is-unlocked');
    }
    if (consentHint instanceof HTMLElement) {
      const unlockedText = consentHint.dataset.unlockedText || '';
      if (unlockedText) consentHint.textContent = unlockedText;
    }
  };

  // Primary path: sighted users who scroll the bottom-of-terms sentinel into
  // view trigger the IntersectionObserver. This is the original behavior and
  // honors the intent that customers engage with the terms before consenting.
  if (termsEnd && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            unlockConsent();
            observer.disconnect();
          }
        });
      },
      { root: null, threshold: 0.01 }
    );
    observer.observe(termsEnd);
  }

  // WCAG 2.1.1 Keyboard (C-04): keyboard users may Tab through the open terms
  // panel without scrolling, in which case the IntersectionObserver above
  // never fires and the consent checkbox stays permanently disabled — making
  // it impossible for a keyboard-only buyer to submit the engraving order.
  // Add additive focus-based and close-based unlock paths so every interaction
  // model has at least one route to enable consent. The original "user must
  // engage with the terms" intent is preserved: keyboard users have to Tab
  // *into* the panel content (past the <summary>), and either path requires
  // having opened the panel first.
  if (termsDetails) {
    let termsHasBeenOpened = termsDetails.open === true;

    termsDetails.addEventListener('toggle', () => {
      if (termsDetails.open) {
        termsHasBeenOpened = true;
      } else if (termsHasBeenOpened) {
        // User opened the terms panel then closed it — treat as acknowledged.
        unlockConsent();
      }
    });

    termsDetails.addEventListener('focusin', (event) => {
      if (!termsDetails.open) return;
      const target = event.target;
      if (target instanceof HTMLElement && target.tagName !== 'SUMMARY') {
        unlockConsent();
      }
    });
  }

  // ---------- Variant sync ----------
  const pdpForm = findPdpProductForm(root);
  /**
   * Read the currently-selected variant id from the live PDP form and mirror
   * it into our hidden input. Called on load + on variant:update events.
   */
  const syncVariantId = () => {
    if (!variantInput) return;
    if (pdpForm) {
      const pdpId = /** @type {HTMLInputElement | null} */ (pdpForm.querySelector('input[name="id"]'));
      if (pdpId && pdpId.value) {
        variantInput.value = pdpId.value;
        return;
      }
    }
    // Fallback: if the PDP form is missing, keep whatever the server rendered.
  };
  syncVariantId();

  document.addEventListener(ThemeEvents.variantUpdate, () => {
    // Give the DOM a tick to settle before reading the updated hidden input.
    requestAnimationFrame(syncVariantId);
  });

  // ---------- Sides radio: update fee readout ----------
  sides.forEach((input) => {
    input.addEventListener('change', () => {
      const display = input.dataset.feeDisplay || '';
      if (feeDisplay && display) {
        // Replace just the currency portion of the existing template string.
        // The Liquid `price_prefix` key wraps the price in a phrase like
        // "Engraving fee <price>", so we re-render the full string by
        // swapping out the current money substring. Simplest + safest:
        // read the data attribute on the variant-price span in the radio
        // card and set the summary text from the radio's visible price.
        const radioPrice = input
          .closest('label')
          ?.querySelector('.engraving-personalize__radio-price');
        if (radioPrice) {
          feeDisplay.textContent = radioPrice.textContent?.trim() || display;
        } else {
          feeDisplay.textContent = display;
        }
      }
    });
  });

  // ---------- File validation ----------
  /**
   * @param {File} file
   * @returns {string | null} error key or null if valid
   */
  const validateFile = (file) => {
    if (!file) return 'missing';
    const typeOk =
      ALLOWED_TYPES.includes(file.type) ||
      ALLOWED_EXTENSIONS.test(file.name || '');
    if (!typeOk) return 'format';
    if (file.size > MAX_FILE_BYTES) return 'size';
    return null;
  };

  const setUploadError = (key) => {
    if (!uploadError) return;
    if (!key) {
      uploadError.hidden = true;
      uploadError.textContent = '';
      return;
    }
    const map = {
      missing: root.dataset.errMissing || 'Please upload a scan of your handwritten message.',
      format: root.dataset.errFormat || 'Please upload a JPG, PNG, or PDF file.',
      size: root.dataset.errSize || 'File is too large. Maximum size is 15 MB.',
    };
    const message = map[key] || map.missing;
    uploadError.hidden = false;
    uploadError.textContent = '';
    requestAnimationFrame(() => {
      if (!uploadError.hidden) uploadError.textContent = message;
    });
  };

  const showFile = (file) => {
    if (filenameEl) filenameEl.textContent = file.name;
    if (emptyState) emptyState.hidden = true;
    if (filledState) filledState.hidden = false;
    dropzone?.classList.add('is-filled');
  };

  const clearFile = () => {
    if (fileInput) fileInput.value = '';
    if (filenameEl) filenameEl.textContent = '';
    if (emptyState) emptyState.hidden = false;
    if (filledState) filledState.hidden = true;
    dropzone?.classList.remove('is-filled');
    setUploadError(null);
  };

  fileInput?.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (!file) {
      clearFile();
      return;
    }
    const err = validateFile(file);
    if (err) {
      setUploadError(err);
      clearFile();
      return;
    }
    setUploadError(null);
    showFile(file);
  });

  removeBtn?.addEventListener('click', (event) => {
    event.preventDefault();
    clearFile();
    fileInput?.focus();
  });

  // ---------- Drag & drop on dropzone ----------
  if (dropzone && fileInput) {
    const stop = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    ['dragenter', 'dragover'].forEach((evt) =>
      dropzone.addEventListener(evt, (e) => {
        stop(e);
        dropzone.classList.add('is-dragover');
      })
    );
    ['dragleave', 'drop'].forEach((evt) =>
      dropzone.addEventListener(evt, (e) => {
        stop(e);
        dropzone.classList.remove('is-dragover');
      })
    );
    dropzone.addEventListener('drop', (e) => {
      const dt = /** @type {DragEvent} */ (e).dataTransfer;
      if (!dt || !dt.files || dt.files.length === 0) return;
      const file = dt.files[0];
      const err = validateFile(file);
      if (err) {
        setUploadError(err);
        return;
      }
      // Assign the dropped file to the input
      const transfer = new DataTransfer();
      transfer.items.add(file);
      fileInput.files = transfer.files;
      fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // ---------- Submit ----------
  let submitHadFocus = false;
  const setLoading = (loading) => {
    if (!submitBtn) return;
    if (loading) submitHadFocus = document.activeElement === submitBtn;
    submitBtn.disabled = loading;
    if (submitDefault) submitDefault.toggleAttribute('hidden', loading);
    if (submitLoading) submitLoading.toggleAttribute('hidden', !loading);
    if (!loading && submitHadFocus) {
      submitHadFocus = false;
      const active = document.activeElement;
      if (!active || active === document.body) submitBtn.focus();
    }
  };

  const showSubmitError = (message) => {
    if (!submitError) return;
    const text = message || root.dataset.errSubmit || 'Something went wrong. Please try again.';
    submitError.hidden = false;
    submitError.textContent = '';
    requestAnimationFrame(() => {
      if (!submitError.hidden) submitError.textContent = text;
    });
  };

  const clearSubmitError = () => {
    if (!submitError) return;
    submitError.hidden = true;
    submitError.textContent = '';
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    clearSubmitError();

    // Ensure variant id is fresh at submit time
    syncVariantId();

    // Client-side validation: file
    const file = fileInput?.files?.[0];
    const fileErr = validateFile(file);
    if (fileErr) {
      setUploadError(fileErr);
      fileInput?.focus();
      return;
    }

    // Consent: checkbox must be unlocked (i.e. user scrolled through terms)
    // AND ticked. If the checkbox is still disabled, guide them to the
    // accordion by opening it and scrolling it into view.
    if (consent && consent.disabled) {
      if (termsDetails && !termsDetails.open) {
        termsDetails.open = true;
      }
      termsDetails?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
      showSubmitError(
        root.dataset.errConsentLocked ||
          'Please open and read the important information above before continuing.'
      );
      return;
    }

    if (consent && !consent.checked) {
      consent.focus();
      showSubmitError(
        root.dataset.errConsent ||
          'Please confirm you accept the engraving terms to continue.'
      );
      return;
    }

    // Variant id sanity check
    if (!variantInput?.value) {
      showSubmitError(
        root.dataset.errVariant ||
          'Please choose your size or options on the product first.'
      );
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData(form);

      // Inject the estimated delivery date as a line-item property so the
      // atelier and concierge teams see it in the order backend. Uses the
      // customer's desired need-by date if they picked one (and it's within
      // the standard window), otherwise the default six-week estimate
      // rendered server-side in data-max-date.
      const estimatedIso = (() => {
        const desired = needbyInput?.value || '';
        if (desired && maxDate && desired >= maxDate) return desired;
        if (desired && !maxDate) return desired;
        return maxDate || desired || '';
      })();
      if (estimatedIso) {
        formData.set(
          'properties[Estimated delivery]',
          formatDate(estimatedIso, locale)
        );
        // Hidden ISO copy (leading underscore keeps it out of the customer
        // view) so the atelier/backend still has a machine-readable date.
        formData.set('properties[_Estimated delivery ISO]', estimatedIso);
      }
      // Rename the need-by field into something clearer in the cart and in
      // the order admin without changing the HTML input name (which stays
      // semantic for the form).
      const desiredValue = needbyInput?.value || '';
      if (desiredValue) {
        formData.delete('properties[Need by]');
        formData.set(
          'properties[Desired delivery date]',
          formatDate(desiredValue, locale)
        );
        formData.set('properties[_Desired delivery ISO]', desiredValue);
      } else {
        formData.delete('properties[Need by]');
      }

      // Append cart-drawer section ids so Shopify returns fresh markup.
      const cartItems = document.querySelectorAll('cart-items-component');
      const ids = [];
      cartItems.forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) {
          ids.push(el.dataset.sectionId);
        }
      });
      if (ids.length > 0) {
        formData.append('sections', ids.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });

      const response = await fetch(
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) ||
          '/cart/add.js',
        {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: 'text/html',
          },
        }
      );

      const json = await response.json();

      if (json.status || json.error || !response.ok) {
        const message = json.description || json.message || json.error || '';
        root.dispatchEvent(
          new CartErrorEvent(form.id || 'engraving-personalize', message, message, json.errors)
        );
        showSubmitError(message);
        return;
      }

      // Pricing is applied server-side by the "engraving-fee-transform" Cart
      // Transform Function (Shopify Functions). It reads the line item's
      // `Engraving` property, looks up shop.metafields.custom.engraving_fee_*,
      // and bumps the bangle line item price so the cart total reflects the
      // engraving cost. No second line item needed — customer sees one line
      // at the combined price.

      // Success — bubble CartAddEvent so the cart drawer opens
      root.dispatchEvent(
        new CartAddEvent({}, 'engraving-personalize', {
          source: 'engraving-personalize',
          itemCount: 1,
          productId: root.dataset.productId,
          variantId: variantInput.value,
          sections: json.sections,
        })
      );

      // Reset UI so the customer can add another if they want
      form.reset();
      clearFile();
      // Re-tick the default side
      const defaultSide = root.querySelector('[data-engraving-side][checked]');
      if (defaultSide instanceof HTMLInputElement) {
        defaultSide.checked = true;
        defaultSide.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } catch (err) {
      console.error('[engraving-personalize]', err);
      showSubmitError();
    } finally {
      setLoading(false);
    }
  });
}

// ---------- Bootstrap ----------
const bootstrap = () => {
  document
    .querySelectorAll('[data-engraving-personalize]')
    .forEach((el) => {
      if (el instanceof HTMLElement && !el.dataset.engravingInitialized) {
        el.dataset.engravingInitialized = 'true';
        initEngravingSection(el);
      }
    });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

// Re-run on Shopify section reload (theme editor)
document.addEventListener('shopify:section:load', bootstrap);
